<template>
  <Modal
    :className="['default-modal', className].join(' ')"
    :isVisible="isVisible"
    :shouldPersistInDom="shouldPersistInDom"
    :shouldPortal="shouldPortal"
    :shouldCloseOnSelect="shouldCloseOnSelect"
    :showOverFlow="showOverFlow"
    @close="$emit('close')"
  >
    <template #header>
      <div class="modal-header__title">{{ steps[state.currentStepIndex].title }}</div>
    </template>
    <template #content>
      <component
        v-if="steps[state.currentStepIndex].content"
        :is="steps[state.currentStepIndex].content.component"
        v-bind="steps[state.currentStepIndex].content.props || {}"
        v-on="steps[state.currentStepIndex].content.events || {}"
        @close="$emit('close')"
      />
    </template>
    <template #closeButton>
      <div class="modal-header__close-button" @click="sendTrackingEvent">
        <CrossIcon fill="var(--color-night-blue-900)olor-night-blue-900)" />
      </div>
    </template>
    <template #footer>
      <component
        v-if="steps[state.currentStepIndex].secondaryAction"
        :is="steps[state.currentStepIndex].secondaryAction.component"
        v-bind="steps[state.currentStepIndex].secondaryAction.props || {}"
        v-on="replaceSpecialEventHandlers(steps[state.currentStepIndex].secondaryAction.events) || {}"
        v-text="steps[state.currentStepIndex].secondaryAction.title"
      />

      <component
        v-if="steps[state.currentStepIndex].primaryAction"
        :is="steps[state.currentStepIndex].primaryAction.component"
        v-bind="steps[state.currentStepIndex].primaryAction.props || {}"
        v-on="replaceSpecialEventHandlers(steps[state.currentStepIndex].primaryAction.events) || {}"
        v-text="steps[state.currentStepIndex].primaryAction.title"
      />
    </template>
  </Modal>
</template>
<script lang="ts" setup>
import { reactive, toRefs, defineAsyncComponent, defineComponent } from 'vue';
import { sendButtonClickedEvent } from '@/resources/tracking';

/*
The ModalWrapper component

- includes some default styling for header, content area, and optional footer area
- accepts an array of steps and thus can handle single and multi-step modals

TODO: add custom validator to "steps" prop

*/

const Modal = defineAsyncComponent(() => import('@/components/molecules/modals/Modal.vue'));
const CrossIcon = defineAsyncComponent(() => import('@/assets/icons/icon--cross.svg?component'));
const emits = defineEmits(['close']);
const props = defineProps({
  className: {
    type: String
  },
  isVisible: {
    type: Boolean,
    default: false
  },
  steps: {
    type: Array,
    default: []
  },
  startAt: {
    type: Number,
    default: 0
  },
  showOnline: {
    type: Boolean,
    default: true
  },
  shouldPersistInDom: {
    type: Boolean,
    default: false
  },
  shouldPortal: {
    type: Boolean,
    default: true
  },
  shouldCloseOnSelect: {
    type: Boolean,
    default: false
  },
  showOverFlow: {
    type: Boolean,
    default: true
  }
});
const state = reactive({
  currentStepIndex: props.startAt
});

function goToPreviousStep() {
  if (state.currentStepIndex === 0) {
    return;
  }

  state.currentStepIndex--;
}

function goToNextStep() {
  if (props.steps.length - 1 === state.currentStepIndex) {
    return;
  }

  state.currentStepIndex++;
}

// In order to simplify usage of this component, we would like to keep track of the currentStepIndex within this
// component. Therefore there is no way for outer components to switch steps, i.e. increase / descrease this index.
//
// But the outer component provides all the steps, including primaryAction and secondaryAction for each step.
// Therefore the outer component needs a way to specify click event handlers. There are two special event handler
// names for now ("goToPreviousStep", "goToNextStep") that outer components can use within there steps definition
// in order to tell this component to navigate between steps.
//
// This method here will be used to replace these string-based event handlers with their final implemenation.
const replaceSpecialEventHandlers = (eventHandlerMap) => {
  for (const [key, value] of Object.entries(eventHandlerMap)) {
    switch (value) {
      case 'goToPreviousStep':
        eventHandlerMap[key] = goToPreviousStep;
        break;

      case 'goToNextStep':
        eventHandlerMap[key] = goToNextStep;
        break;
    }
  }

  return eventHandlerMap;
};

const { $router } = useNuxtApp();

function sendTrackingEvent() {
  let buttonAction;
  if (props.steps.length === 2) {
    if (props.startAt === 0) {
      buttonAction = 'Close City Selector';
    } else {
      buttonAction = 'Close Date Selector';
    }
  }
  sendButtonClickedEvent({
    label: 'Close',
    citySelected: 'no info',
    listId: ['no info'],
    category: ['no info'],
    elementId: 'no info',
    hostId: 'no info',
    locationCity: ['no info'],
    motivLevel: ['no info'],
    itemName: 'no info',
    buttonAction: buttonAction || 'close modal',
    value: 'val'
  });
  emits('close');
}
</script>
